import React, { useMemo } from "react";
import { Outlet } from "react-router-dom";

// material-ui
import { AppBar, Backdrop, Box, CircularProgress, Container, CssBaseline, Toolbar, useMediaQuery } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
// project imports
import useConfig from "hooks/useConfig";
import { useIsHandyStyle } from "hooks/useWhiteLabelBranding";
import navigation from "menu-items/menuItemsAdmin";
import { useDispatch, useSelector } from "store";
import { drawerWidth } from "store/constant";
import { openDrawer } from "store/slices/menu";
import Breadcrumbs from "ui-component/extended/Breadcrumbs";
import Header from "./Header";
import Sidebar from "./Sidebar";

// assets
import { IconChevronRight } from "@tabler/icons";
import { useNegocioContext } from "contexts/NegocioContext";

// styles
const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })(({ theme, open }) => ({
  ...theme.typography.mainContent,
  ...(!open && {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    borderRadius: 16,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.shorter,
    }),
    [theme.breakpoints.up("md")]: {
      marginLeft: -(drawerWidth - 20),
      width: `calc(100% - ${drawerWidth}px)`,
    },
    [theme.breakpoints.down("md")]: {
      marginLeft: "20px",
      width: `calc(100% - ${drawerWidth}px)`,
      padding: "16px",
    },
    [theme.breakpoints.down("sm")]: {
      marginLeft: "10px",
      width: `calc(100% - ${drawerWidth}px)`,
      padding: "16px",
      marginRight: "10px",
    },
  }),
  ...(open && {
    borderRadius: 16,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.shorter,
    }),
    marginLeft: 0,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    width: `calc(100% - ${drawerWidth}px)`,
    [theme.breakpoints.down("md")]: {
      marginLeft: "20px",
    },
    [theme.breakpoints.down("sm")]: {
      marginLeft: "10px",
    },
  }),
}));

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
  const theme = useTheme();
  const matchDownMd = useMediaQuery(theme.breakpoints.down("lg"));
  const dispatch = useDispatch();
  const { drawerOpen } = useSelector((state) => state.menu);
  const { container } = useConfig();
  const { negocio } = useNegocioContext();
  const globalState = useSelector((state) => state.global);
  const { isGlobalLoading } = globalState;
  const isHandyStyle = useIsHandyStyle();

  // si la ruta es / redirect a dashboard
  React.useEffect(() => {
    const pathname = window.location;
    if (pathname.pathname === "/") {
      window.location.href = "/dashboard/default";
    }
  }, []);

  React.useEffect(() => {
    dispatch(openDrawer(!matchDownMd));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchDownMd]);

  const header = useMemo(
    () => (
      <Toolbar
        sx={{
          minHeight: "56px !important",
          padding: "10px !important",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "56px",
          zIndex: 1000,
        }}>
        <Box sx={{ width: "100%", height: "100%" }}>
          <Header />
        </Box>
      </Toolbar>
    ),
    []
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      {/* loader global */}
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isGlobalLoading} aria-label="Cargando contenido">
        <CircularProgress />
      </Backdrop>
      {/* header */}
      <AppBar
        enableColorOnDark
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          bgcolor: isHandyStyle ? theme.palette.primary.main : theme.palette.background.default,
          transition: drawerOpen ? theme.transitions.create("width") : "none",
          minHeight: "56px",
          "& .MuiToolbar-root": {
            minHeight: "56px !important",
            padding: 0,
          },
        }}>
        {header}
      </AppBar>

      {/* drawer */}
      <Sidebar negocio={negocio} />

      {/* main content */}
      <Main theme={theme} open={drawerOpen} sx={{ borderRadius: isHandyStyle ? 0 : "16px" }}>
        {/* breadcrumb */}
        {container && (
          <Container maxWidth="lg">
            <Breadcrumbs separator={IconChevronRight} navigation={navigation} icon title rightAlign />
            <Outlet />
          </Container>
        )}
        {!container && (
          <>
            <Breadcrumbs separator={IconChevronRight} navigation={navigation} icon title rightAlign />
            <Outlet />
          </>
        )}
      </Main>
    </Box>
  );
};

export default MainLayout;
